import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Download, Send, Edit } from "lucide-react";
import { usePDF } from "react-to-pdf";

export function InquiryItineraryView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: inquiry } = useQuery({
    queryKey: ["inquiry", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inquiry")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const { toPDF, targetRef } = usePDF({
    filename: inquiry ? `${inquiry?.name || id}-itinerary.pdf` : `itinerary.pdf`,
    page: { margin: 20 }
  });

  // Helper function to clean title by removing transport price information
  const cleanTitle = (title: string) => {
    if (!title) return title;
    // Remove only transport cost information, keep the area
    return title
      .replace(/ - Transport: (Rp )?[\d.,]+/g, '')  // Format: " - Transport: Rp 135000" or " - Transport: 135000"
      .replace(/ - Transport Cost: [\d.,]+/g, '');   // Format: " - Transport Cost: 135000"
  };

  if (!inquiry) return null;

  // Parse hotel_json if it exists
  const hotelInfo = inquiry.hotel_json ? 
    (typeof inquiry.hotel_json === 'string' ? 
      JSON.parse(inquiry.hotel_json) : 
      inquiry.hotel_json) : 
    null;

  // Function to handle PDF generation
  const handleGeneratePDF = () => {
    // Add a small delay to ensure CSS transitions complete
    setTimeout(() => {
      toPDF();
    }, 100);
  };

  return (
    <div>
      <div id="inquiry-content" ref={targetRef} className="container mx-auto py-2 space-y-2 max-w-[210mm]">


        <Card className="shadow-sm p-4 bg-primary/5">
          <CardContent className="p-0 flex justify-center">
            <h2 className="text-3xl font-bold text-primary">Bali Tour Itinerary: {inquiry.voucher_code}</h2>
          </CardContent>
        </Card>

        <Card className="shadow-sm p-1">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Guest Information</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            <Table className="border-collapse border border-gray-800">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Name</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Guest</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Days</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Date</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Car Type</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Flight Information</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.name}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">
                    {inquiry.adult_count} Adult & {inquiry.children_count} Child
                  </TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.nights}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.date ? format(new Date(inquiry.date), "dd MMM yyyy") : "-"}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.car_type || "-"}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 whitespace-pre-line">{inquiry.flight_information ?? "-"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Hotel Information Section */}
        <Card className="shadow-sm p-1">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Hotel Information</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            {hotelInfo && hotelInfo.hotels && Object.keys(hotelInfo.hotels).length > 0 ? (
              <Table className="border-collapse border border-gray-800">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-4 p-2 text-xs border border-gray-800">Hotel Name</TableHead>
                    <TableHead className="h-4 p-2 text-xs border border-gray-800">Check-in</TableHead>
                    <TableHead className="h-4 p-2 text-xs border border-gray-800">Check-out</TableHead>
                    <TableHead className="h-4 p-2 text-xs border border-gray-800">Nights</TableHead>
                    <TableHead className="h-4 p-2 text-xs border border-gray-800">Rooms</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(hotelInfo.hotels).map(([day, hotel]) => (
                    <TableRow key={day} className="hover:bg-transparent">
                      <TableCell className="h-4 p-2 text-xs border border-gray-800">{(hotel as any).hotel_name}</TableCell>
                      <TableCell className="h-4 p-2 text-xs border border-gray-800">{(hotel as any).checkin_date}</TableCell>
                      <TableCell className="h-4 p-2 text-xs border border-gray-800">{(hotel as any).checkout_date}</TableCell>
                      <TableCell className="h-4 p-2 text-xs border border-gray-800">{(hotel as any).nights}</TableCell>
                      <TableCell className="h-4 p-2 text-xs border border-gray-800">{(hotel as any).room_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">No hotel information available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Remarks Section */}
        <Card className="shadow-sm p-1">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Remarks</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            <div className="p-2 border border-gray-800 rounded-md">
              <p className="text-xs whitespace-pre-line">{inquiry.remarks || "No remarks available"}</p>
            </div>
          </CardContent>
        </Card>

        {/* Tour Itinerary Information */}
        {inquiry.tour_package_json && Object.entries(inquiry.tour_package_json).length > 0 && (
          <Card className="shadow-sm p-1">
            <CardHeader className="p-1">
              <CardTitle className="text-sm font-medium">Tour Itinerary Information</CardTitle>
            </CardHeader>
            <CardContent className="p-1">
              <div className="space-y-4">
                {Object.entries(inquiry.tour_package_json).map(([day, data]) => (
                  <div key={day} className="p-2 border border-gray-800 rounded-md">
                    <h3 className="text-xs font-bold mb-2">
                      {(() => {
                        // Get the original title
                        const originalTitle = (data as any).title || (data as any).captured_tours?.[day]?.title;
                        
                        if (originalTitle) {
                          return cleanTitle(originalTitle);
                        }
                        
                        // Fallback if no title exists
                        return `Day ${day.split('_')[1]} - ${typeof data === 'object' && 'day_date' in data ? String(data.day_date) : ''}`;
                      })()}
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs whitespace-pre-line" style={{ lineHeight: '2em' }}>{typeof data === 'object' && 'itinerary' in data ? String(data.itinerary) : ''}</p>
                      </div>
                      {/* Day Notes */}
                      {typeof data === 'object' && 'note' in data && data.note && (
                        <div className="mt-4">
                          <h4 className="text-xs font-semibold mb-1">Notes:</h4>
                          <p className="text-xs whitespace-pre-line pl-2 border-l-2 border-primary">{String(data.note)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4 mt-8 print:hidden sticky bottom-0 w-full bg-white border-t shadow-lg p-4">
        <Button 
          variant="outline" 
          onClick={handleGeneratePDF}
          className="print:hidden"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
        <Button 
          onClick={() => navigate(`/inquiry-itinerary-edit/${id}`)}
          className="print:hidden"
        >
          <Edit className="w-4 h-4 mr-2" />
          Edit Itinerary
        </Button>
      </div>
    </div>
  );
}
